/**
 * Phase 2 MM13 — Supplier Directory (workspace-scoped, internal).
 * Real API-backed list + search + create + profile panel. Honesty:
 * internal directory only, certifications are unverified metadata, no
 * automatic supplier approval.
 */

import { useCallback, useEffect, useState } from "react";
import {
  archiveSupplier, createSupplier, getSupplier, getSuppliers, getSupplierStatus,
} from "../api/client";
import { Button } from "../components/Button";
import { StatusPill } from "../components/StatusPill";
import { studioStrings } from "../i18n/studioStrings";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";

type Row = Record<string, unknown>;
const TYPES = ["printer", "packaging_converter", "paper_board_supplier",
  "rigid_box_manufacturer", "flexible_packaging_supplier",
  "label_sticker_supplier", "bag_supplier", "carton_supplier",
  "specialty_finish_supplier", "broker_agent", "other"];
const OBJECTS = ["burger_box", "delivery_bag", "sticker", "label",
  "folding_carton", "rigid_box", "pouch", "mailer_box", "cup", "other"];

export function SupplierDirectoryPage(): JSX.Element {
  const [locale] = useLocale();
  const t = studioStrings(locale);
  const [items, setItems] = useState<Row[]>([]);
  const [state, setState] = useState<"loading" | "ok" | "denied" | "error">("loading");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Row | null>(null);
  const [certs, setCerts] = useState<Row[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", supplier_type: "printer",
    country: "", city: "", object_types: [] as string[] });

  const refresh = useCallback(async () => {
    const [st, r] = await Promise.all([
      getSupplierStatus(), getSuppliers(search || undefined)]);
    if (st.outcome !== "ok" && st.status === 401) { setState("denied"); return; }
    if (r.outcome === "ok") { setItems(r.data?.items ?? []); setState("ok"); }
    else setState(r.status === 403 ? "denied" : "error");
  }, [search]);

  useEffect(() => { void refresh(); }, [refresh]);

  async function openProfile(id: string): Promise<void> {
    const r = await getSupplier(id);
    if (r.outcome === "ok") {
      setSelected(r.data?.item ?? null);
      setCerts(r.data?.certifications ?? []);
    }
  }

  async function onCreate(): Promise<void> {
    if (form.name.trim().length < 2) { setNotice(t.loadError); return; }
    const r = await createSupplier(form);
    setNotice(r.outcome === "ok" ? null
      : r.status === 403 ? t.permissionDenied : r.errorMessage);
    if (r.outcome === "ok") setForm({ ...form, name: "", object_types: [] });
    await refresh();
  }

  return (
    <AppLayout>
      <h1>{t.suppliersTitle}</h1>
      <p className="po-disclaimer" data-testid="supplier-honesty">
        {t.notSupplierApproval}
      </p>
      {notice ? <p role="alert" className="po-error">{notice}</p> : null}
      {state === "denied" ? <p role="alert">{t.permissionDenied}</p> : null}
      {state === "error" ? <p role="alert">{t.loadError}</p> : null}
      {state === "loading" ? <p role="status">…</p> : null}

      <section className="po-row" aria-label={t.newSupplier} data-testid="supplier-create">
        <input placeholder={t.nameLabel} value={form.name} data-testid="supplier-name"
               onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <select value={form.supplier_type}
                onChange={(e) => setForm({ ...form, supplier_type: e.target.value })}>
          {TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <input placeholder={t.countryLabel} value={form.country}
               onChange={(e) => setForm({ ...form, country: e.target.value })} />
        <input placeholder={t.cityLabel} value={form.city}
               onChange={(e) => setForm({ ...form, city: e.target.value })} />
        <select multiple value={form.object_types} aria-label="object types"
                onChange={(e) => setForm({ ...form, object_types:
                  Array.from(e.target.selectedOptions, (o) => o.value) })}>
          {OBJECTS.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <Button variant="primary" onClick={() => void onCreate()}>{t.newSupplier}</Button>
      </section>

      <section className="po-row">
        <input placeholder={t.searchLabel} value={search} data-testid="supplier-search"
               onChange={(e) => setSearch(e.target.value)} />
      </section>

      {state === "ok" && items.length === 0 ? (
        <p data-testid="suppliers-empty">{t.emptyList}</p>
      ) : (
        <table className="po-table" data-testid="suppliers-table">
          <thead><tr><th>{t.nameLabel}</th><th>{t.typeLabel}</th>
            <th>{t.countryLabel}</th><th>{t.statusLabel}</th><th /></tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={String(s.id)} onClick={() => void openProfile(String(s.id))}
                  style={{ cursor: "pointer" }}>
                <td>{String(s.name)}</td><td>{String(s.supplier_type)}</td>
                <td>{String(s.country || "—")}</td>
                <td><StatusPill tone={s.status === "active" ? "ok"
                  : s.status === "blocked" ? "warn" : "info"}
                                label={String(s.status)} /></td>
                <td><Button variant="secondary" onClick={(e) => {
                  e.stopPropagation();
                  void archiveSupplier(String(s.id)).then(refresh);
                }}>{t.archive}</Button></td>
              </tr>))}
          </tbody>
        </table>)}

      {selected ? (
        <section data-testid="supplier-profile">
          <h2>{String(selected.name)}</h2>
          <p>{String(selected.supplier_type)} · {String(selected.country || "—")}
            {" "}· MOQ {String(selected.moq_min ?? "—")}</p>
          <p>{t.fitScore} objects: {(selected.object_types as string[] ?? []).join(", ") || "—"}</p>
          <h3>Certifications <small>({certs.length}) — unverified metadata</small></h3>
          <ul>
            {certs.map((c) => (
              <li key={String(c.id)}>{String(c.cert_name)} —{" "}
                <StatusPill tone={c.verification_status === "internally_reviewed"
                  ? "ok" : "info"} label={String(c.verification_status)} /></li>))}
            {certs.length === 0 ? <li>{t.emptyList}</li> : null}
          </ul>
        </section>
      ) : null}
    </AppLayout>
  );
}
